import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Flame, FileText } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { EngineDay, StatusPill } from "./EngineDay";
import { StagingPanel } from "./StagingPanel";

export async function postAction(n, payload) {
  try {
    const { data } = await api.post(`/c2g/day/${n}`, payload);
    return data;
  } catch (e) {
    toast.error(apiError(e.response?.data?.detail) || "Errore nel salvataggio");
    return null;
  }
}

export function OggiEngine({ path, day, times, onViewChange, reload }) {
  const navigate = useNavigate();
  const view = day;
  const c2g = path.profile.c2g;
  const todayLabel = path.calendar.days[view.day_number - 1]?.label || "";

  const onAction = async (payload) => {
    const data = await postAction(view.day_number, payload);
    if (!data) return false;
    onViewChange(data);
    if (payload.action === "close") toast.success("Giornata conclusa");
    return true;
  };

  return (
    <div className="px-5 pt-6">
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="relative overflow-hidden rounded-2xl border border-[#27272A] bg-[#121215] p-6">
        <span className="pointer-events-none absolute -right-16 -top-16 h-44 w-44 rounded-full bg-[#D4AF37]/12 blur-3xl" />
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2" data-testid="c2g-profile">
              <span className="break-words text-xl font-bold uppercase tracking-wide text-[#FAF9F6] sm:text-2xl" data-testid="c2g-profile-dimension">{c2g.dimension}</span>
              <span className="rounded-md border border-[#D4AF37] px-2 py-0.5 text-sm font-bold uppercase tracking-wider text-[#E5C158]" data-testid="c2g-profile-level">{c2g.level_label}</span>
            </div>
            <p className="mt-3 text-sm font-semibold text-[#D4D4D8]" data-testid="c2g-current-day">Settimana {path.week} · Giorno {view.day_number} di 7 · {todayLabel}</p>
            {c2g.week_title && <p className="mt-1 text-sm text-[#B4B4BC]" data-testid="c2g-week-title">{c2g.week_title}</p>}
            <h1 className="mt-1 text-[22px] font-bold leading-snug text-[#FAF9F6]" data-testid="c2g-day-title">{view.day.title}</h1>
            <p className="mt-3 a2g-read text-base leading-relaxed text-[#B4B4BC]" data-testid="c2g-objective"><b className="text-[#D4D4D8]">Obiettivo:</b> {c2g.objective}</p>
          </div>
          <div className="flex w-20 shrink-0 flex-col items-center rounded-2xl border border-[#D4AF37]/30 bg-gradient-to-br from-[#18181C] to-[#0D0D0F] px-2 py-4 text-center sm:w-24 sm:px-3">
            <Flame size={18} className="text-[#D4AF37]" />
            <span className="tabular-nums mt-1 text-3xl font-semibold text-[#FAF9F6]" data-testid="streak-count">{path.stats.days_lived}/7</span>
            <span className="mt-1 text-[12px] uppercase leading-tight tracking-wider text-[#8A8E96]">Giorni conclusi</span>
          </div>
        </div>
        <StatusPill view={view} />
      </motion.div>

      <div className="mt-5">
        <EngineDay view={view} times={times} onAction={onAction} />
      </div>

      {view.day_number === 7 && view.record?.evening?.closed && (
        <button onClick={() => navigate("/riepilogo")} data-testid="c2g-open-report" className="btn-gold mt-6 flex w-full items-center justify-center gap-2 rounded-full px-5 py-3 text-sm font-semibold uppercase tracking-wide">
          <FileText size={16} /> Il report del Giorno 7
        </button>
      )}

      <StagingPanel key={view.record?.updatedAt || "none"} onChange={reload} />
    </div>
  );
}
